import {
  Money,
  Weight,
  IdName,
  Dimensions,
  ErrorDetail,
  StringIdName,
  ErrorResponse,
  ItemsListData,
  ContactsService,
  InventoryService,
  PipelineConfiguration,
  ConfigurationProperty,
  ConfigurationParameter,
  ItemInventoryWithWarehouse,
  PipelinesConfigurationResponse,
  ItemsMappingItemVendorResponse,
  AmazonInboundUpdatePrepDetailsItemRequest,
} from "../api-client";
import {
  AdministrationService,
  AppIntegrationService,
} from "../api-client/sdk.gen";
import type {
  RowType,
  LookupValues,
  FormValuesType,
  valueSortedType,
  ViewOptionsType,
  NestedOptionType,
  DefaultOptionsType,
  ValueLabelStringType,
} from "./types/shared/miscellaneous";
import { toast } from "sonner";
import debounce from "debounce-promise";
import { NavigateFunction } from "react-router-dom";
import { StylesConfig, GroupBase } from "react-select";
import type { InputValuesType } from "./types/shared/inputValues";
import type { SyncType } from "./types/pipelineConfiguration/settings";
import type { ValueInputChangeType } from "./types/shared/customSelect";
import type { TableColumnType, TablePeriodsType } from "./types/listTable";
import { ChangeEvent, Dispatch, KeyboardEvent, MutableRefObject } from "react";

const wait = 700;

function yearLength(locale: string): number {
  return Intl.DateTimeFormat(locale).resolvedOptions().year === "numeric"
    ? 4
    : 2;
}

export const datePickerFormat = (locale: string) => {
  const sampleDate = new Date(2025, 11, 31);

  const dtf = new Intl.DateTimeFormat(locale, { dateStyle: "short" });

  const year_length = yearLength(locale);

  const parts = dtf.formatToParts(sampleDate);

  let pattern = "";
  for (const part of parts) {
    switch (part.type) {
      case "day":
        pattern += "d";
        break;
      case "month":
        pattern += "m";
        break;
      case "year":
        pattern += year_length == 4 ? "Y" : "y";
        break;
      case "literal":
      default:
        pattern += part.value;
        break;
    }
  }

  return pattern;
};

export const datePickerPlaceholderFormat = (locale: string) => {
  const culture = new Intl.DateTimeFormat(locale).resolvedOptions();

  const dtf = new Intl.DateTimeFormat(locale, { dateStyle: "short" });

  const sampleDate = new Date(2025, 11, 31);

  const parts = dtf.formatToParts(sampleDate);

  let pattern = "";
  for (const part of parts) {
    switch (part.type) {
      case "day":
        const day_length = 2;
        pattern += "d".repeat(day_length);
        break;
      case "month":
        const month_length = 2;
        pattern += "m".repeat(month_length);
        break;
      case "year":
        const year_length = culture.year == "numeric" ? 4 : 2;
        pattern += "y".repeat(year_length);
        break;
      case "literal":
      default:
        pattern += part.value;
        break;
    }
  }

  return pattern;
};

const loadItemOptions = async (value: string): Promise<LookupValues[]> => {
  try {
    if (value) {
      const { data, error } = await InventoryService.itemLookup({
        query: { query: value },
      });

      if (error || !data?.data) {
        console.error("Error fetching item options:", error);
        return [];
      }

      return data.data.map((t) => ({
        value: t.sku,
        label: t.sku,
        title: t.title,
        id: t.id,
      }));
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

export const debouncedLoadItemOptions = debounce(loadItemOptions, wait);

export const getItemOption = async (
  values: string[] | undefined
): Promise<LookupValues[]> => {
  const defaultOptions: LookupValues[] = [];

  if (values) {
    for (let x = 0; x < values.length; x++) {
      // fetch the option with the value name
      const value = values[x];
      if (value) {
        const options: LookupValues[] = await loadItemOptions(value);
        const option = options[0];

        if (option) {
          defaultOptions.push(option);
        }
      }
    }
  }
  return defaultOptions;
};

const loadContactOptions = async (value: string): Promise<LookupValues[]> => {
  // function used to get input value for contacts input
  try {
    if (value) {
      const { data, error } = await ContactsService.contactsLookup({
        query: { query: value },
      });

      if (error || !data?.results) {
        console.error("Error fetching contact options:", error);
        return [];
      }

      return data.results.map((t) => ({
        value: t.id,
        label: t.codeName,
      }));
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

export const debouncedLoadContactOptions = debounce(loadContactOptions, wait);

export const getContactValue = async (
  values: string[] | undefined
): Promise<ValueLabelStringType[]> => {
  const resultsArray: ValueLabelStringType[] = [];

  if (values) {
    for (let x = 0; x < values.length; x++) {
      // fetch the option with the value name
      const value = values[x];
      if (value) {
        const { data, error } = await ContactsService.contactsLookup({
          query: { query: value },
        });
        if (error) {
          console.error("Error fetching contact options:", error);
          return [];
        }

        if (data?.results && data.results?.length > 0) {
          resultsArray.push({
            value: data.results[0]?.id,
            label: data.results[0]?.codeName!,
          });
        }
      }
    }
  }
  return resultsArray;
};

export const getContactOption = async (
  values: string[] | undefined,
  fieldName: string
): Promise<DefaultOptionsType[]> => {
  const defaultOptions: DefaultOptionsType[] = [];

  if (values) {
    for (let x = 0; x < values.length; x++) {
      // fetch the option with the value name
      const valueToFetch = values[x];
      if (valueToFetch) {
        const options: LookupValues[] | undefined =
          await loadContactOptions(valueToFetch);

        const defaultOption: DefaultOptionsType[] = options
          ? options.map(({ value, label }) => ({
              value,
              label,
              id: valueToFetch,
              fieldName: fieldName,
              isVisible: true,
            }))
          : [];

        defaultOptions.push(...defaultOption);
      }
    }
  }
  return defaultOptions;
};

export const ensureArray = (value: string | string[] | undefined): string[] => {
  if (typeof value === "string") {
    return [value];
  } else if (Array.isArray(value)) {
    return value;
  } else {
    return [];
  }
};

export const shouldIncludeColumn = (
  data: ItemInventoryWithWarehouse[],
  accessor:
    | keyof ItemInventoryWithWarehouse
    | ((row: ItemInventoryWithWarehouse) => any | undefined)
): boolean => {
  return data.some((current) => {
    const value =
      typeof accessor === "function" ? accessor(current) : current[accessor];
    return value !== null && value !== 0;
  });
};

export const as2decimals = (value: number | string): string | number => {
  if (typeof value !== "number") {
    return value;
  } else {
    return new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }
};

const toMax3decimals = new Intl.NumberFormat("en-GB", {
  style: "decimal",
  maximumFractionDigits: 3,
  minimumFractionDigits: 0,
});

export const as3decimals = (
  input: number | undefined
): string | number | null => {
  if (input != null && input !== undefined) {
    return toMax3decimals.format(input);
  }
  return null;
};

const moneyFormat = new Intl.NumberFormat("en-GB", {
  style: "decimal",
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

export const asMoney = (
  input: number | Money | undefined | null
): string | null => {
  if (input == null || input == undefined) return null;

  if (typeof input === "number") {
    return asCurrency(undefined, input);
  } else {
    return asCurrency(input.currencyCode, input.amount);
  }
};

export const asCurrency = (
  currencyCode: string | undefined | null,
  amount: number | undefined | null
): string | null => {
  if (amount == null || amount == undefined) return null;

  if (currencyCode == null || currencyCode == undefined) {
    return moneyFormat.format(amount);
  } else {
    const _amount = moneyFormat.format(amount);
    const _currencySymbol = getCurrencySymbol(currencyCode);
    if (_currencySymbol) {
      return `${_currencySymbol} ${_amount}`.trim();
    }
    return `${_amount} ${currencyCode}`.trim();
  }
};

export const dimensionsDisplay = (
  input: Dimensions | undefined | null
): string | null => {
  if (input == null || input == undefined) return null;

  let result = "";
  let added = false;

  if (input.width != null) {
    result += `W: ${input.width}`;
    added = true;
  }
  if (input.height != null) {
    if (added) {
      result += " x ";
    }
    result += `H: ${input.height}`;
    added = true;
  }
  if (input.length != null) {
    if (added) {
      result += " x ";
    }
    result += `L: ${input.length}`;
    added = true;
  }

  if (!added) {
    return null;
  }

  if (input.uom != null && input.uom.trim() !== "") {
    result += ` ${input.uom}`;
  }

  return result;
};

const inventoryFormat = new Intl.NumberFormat("en-GB", {
  style: "decimal",
  maximumFractionDigits: 5,
  minimumFractionDigits: 0,
});

export const asInventory = (
  input: number | undefined | null
): string | null => {
  if (input != null && input !== undefined) {
    return inventoryFormat.format(input);
  }
  return null;
};

export const asWeight = (input: Weight | undefined | null): string | null => {
  if (input?.value != null && input !== undefined) {
    return `${inventoryFormat.format(input.value)} ${input.uom}`;
  }
  return null;
};

const mapDataWithHeaders = (
  columns: TableColumnType[],
  data: RowType,
  isInventoryLog: boolean,
  isForecastTable: boolean,
  period?: TablePeriodsType[]
): RowType => {
  const accessorToHeaderMap: { [key: string]: string } = {};

  columns.forEach(({ accessor, Header }) => {
    if (accessor && Header) {
      if (isForecastTable && period?.some((p) => p.name === accessor)) {
        const childrenArray = Header.props.children.map((child: any) => {
          if (Array.isArray(child.props.children)) {
            return "";
          } else {
            return child.props.children;
          }
        });
        accessorToHeaderMap[accessor] = `${childrenArray.join(" ")}`;
      } else if (isForecastTable) {
        if (accessor === "forecastResults.currentInventory") {
          accessorToHeaderMap[accessor] = Header as string;
          accessorToHeaderMap["inventory.onHand"] = "On Hand";
          accessorToHeaderMap["inventory.available"] = "Available";
          accessorToHeaderMap["inventory.onOrder"] = "On Order";
          accessorToHeaderMap["inventory.inTransit"] = "In Transit";
        } else {
          accessorToHeaderMap[accessor] = Header as string;
        }
      } else {
        accessorToHeaderMap[accessor] = Header as string;
      }
    }
  });

  return data.map((row) => {
    const newRow: { [key: string]: any } = {};

    Object.keys(row).forEach((key) => {
      const newKey = accessorToHeaderMap[key] || key;
      const periodKeyParts: string[] = key.split(".");
      const periodName = periodKeyParts[1];
      const periodKey: string = periodKeyParts[2]!;

      if (isInventoryLog) {
        if (newKey !== "warehouse") {
          newRow[newKey] = row[key] as any;
        }
      } else if (
        isForecastTable &&
        period?.some((p) => p.name === periodName)
      ) {
        if (periodTypeOptionsName[periodKey]) {
          const keyName = `${periodTypeOptionsName[periodKey]} ${
            accessorToHeaderMap[periodKeyParts[1] as string]
          }`;
          newRow[keyName] = row[key];
        }
      } else {
        const matchingColumn = [
          ...columns,
          { accessor: "inventory.onHand" },
          { accessor: "inventory.available" },
          { accessor: "inventory.onOrder" },
          { accessor: "inventory.inTransit" },
        ].find((col) => {
          return col.accessor === key;
        });

        if (matchingColumn) {
          newRow[newKey] = row[key];
        }
      }
    });

    if (isForecastTable) {
      return reorderMonthlyFields(newRow);
    }

    return newRow;
  });
};

function reorderMonthlyFields(data: Record<string, any>): Record<string, any> {
  const monthlyEntries: [string, any][] = [];
  const otherEntries: [string, any][] = [];

  for (const [key, value] of Object.entries(data)) {
    if (/ M\d+$/.test(key)) {
      monthlyEntries.push([key, value]);
    } else {
      otherEntries.push([key, value]);
    }
  }

  monthlyEntries.sort((a, b) => {
    const aMonth = parseInt(a[0].match(/ M(\d+)$/)?.[1] || "0", 10);
    const bMonth = parseInt(b[0].match(/ M(\d+)$/)?.[1] || "0", 10);
    return bMonth - aMonth;
  });

  const finalEntries = [
    otherEntries[0],
    otherEntries[1],
    otherEntries[2],
    ...monthlyEntries,
    ...otherEntries.slice(3),
  ];

  const result: Record<string, any> = {};
  for (const [key, value] of finalEntries) {
    result[key] = value;
  }

  return result;
}

export const downloadExcel = async (
  columns: TableColumnType[],
  data: RowType,
  name: string | undefined,
  isInventoryLog: boolean,
  isForecastTable: boolean,
  period?: TablePeriodsType[]
): Promise<void> => {
  const { unparse } = await import("papaparse");
  const { saveAs } = await import("file-saver");
  const XLSX = await import("xlsx");

  const mappedData = mapDataWithHeaders(
    columns,
    data,
    isInventoryLog,
    isForecastTable,
    period
  );

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(mappedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  // Add auto-filters (not natively supported, requires XLSX.write)
  const range = XLSX.utils.decode_range(worksheet["!ref"] || "");
  worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

  // Write the workbook to a file and trigger download
  const buffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    compression: true,
  });
  const dataBlob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });

  const fileName = `${name?.replace(" ", "_")}${new Intl.DateTimeFormat(
    "en-GB"
  ).format(new Date())}.xlsx`;

  saveAs(dataBlob, fileName);
};

export const downloadCSV = async (
  columns: TableColumnType[],
  data: RowType,
  name: string | undefined,
  isInventoryLog: boolean,
  isForecastTable: boolean,
  period?: TablePeriodsType[]
): Promise<void> => {
  const { unparse } = await import("papaparse");
  const { saveAs } = await import("file-saver");

  const mappedData = mapDataWithHeaders(
    columns,
    data,
    isInventoryLog,
    isForecastTable,
    period
  );
  let transformedData;

  if (isInventoryLog) {
    transformedData = mappedData.map(({ row }) => ({
      ...row,
      Batch: row.Batch?.bestBeforeDate,
      Price: `${row.price?.amount ? row.price.amount : ""} ${
        row.price?.currencyCode ? row.price.currencyCode : ""
      }`,
    }));
  }

  const csv = unparse({
    fields: isForecastTable
      ? Object.keys(mappedData[0]!)
      : columns.map((col) => col.Header),
    data: transformedData ? transformedData : mappedData,
  });

  const csvBlob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveAs(
    csvBlob,
    `${name?.replace(" ", "_")}${new Intl.DateTimeFormat("en-GB").format(
      new Date()
    )}.csv`
  );
};

export const getUrl = (data: FormValuesType, parentKey = ""): string => {
  let queryParams = [];

  for (const [key, value] of Object.entries(data)) {
    let currentKey = parentKey ? `${parentKey}.${key}` : key;
    if (typeof value === "object" && !Array.isArray(value)) {
      // If the value is an object, recursively call the function

      queryParams.push(getUrl(value, currentKey));
    } else {
      // Handle arrays by joining them with commas
      if (Array.isArray(value) && value.length === 0) continue;
      if (!Array.isArray(value) && value === "") continue;
      if (typeof value == "boolean" && !value) continue;

      let paramValue = Array.isArray(value) ? value.join(",") : value;
      if (
        (currentKey === "sales.sku" || currentKey === "items.sku") &&
        typeof paramValue === "string"
      ) {
        paramValue = paramValue!.split(" ");
      }

      if (
        data[`dynamicStartingDate`] &&
        currentKey === "date.dynamicStartingDate"
      ) {
        currentKey = "date.start";
      }
      if (
        data[`dynamicEndingDate`] &&
        currentKey === "date.dynamicEndingDate"
      ) {
        currentKey = "date.end";
      }

      queryParams.push(`${currentKey}=${paramValue}`);
    }
  }
  queryParams = queryParams.filter((value) => value !== "");
  return queryParams.join("&");
};

export enum DateFormatTimeMode {
  Minute = "minute",
  Second = "second",
}

export const transformDateFormat = (
  date: Date | string | undefined | null,
  locale: string | undefined | null = undefined,
  time: DateFormatTimeMode | boolean = false
): string => {
  if (!date) return "";

  let dateObj: Date;

  if (typeof date === "string") {
    dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "";
  } else {
    dateObj = date;
  }

  if (time) {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: time === DateFormatTimeMode.Second ? "2-digit" : undefined,
    };
    return dateObj.toLocaleString(locale || undefined, options);
  }

  return dateObj.toLocaleDateString(locale || undefined);
};

export const formatRelativeDate = (date: Date | null | undefined): string => {
  if (!date) return "";

  let dateObj: Date;

  if (typeof date === "string") {
    dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "";
  } else {
    dateObj = date;
  }

  const now = new Date();
  const diff = Math.abs(now.getTime() - dateObj.getTime());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return "Today";
  } else if (days === 1) {
    return "Yesterday";
  } else {
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  }
};

export const formatRelativeTime = (date: Date | null | undefined): string => {
  if (!date) return "";

  let dateObj: Date;

  if (typeof date === "string") {
    dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "";
  } else {
    dateObj = date;
  }

  const now = new Date();

  const diff = Math.abs(now.getTime() - dateObj.getTime());
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  } else if (days < 30) {
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  } else if (months < 12) {
    return `${months} month${months !== 1 ? "s" : ""} ago`;
  } else {
    return `${years} year${years !== 1 ? "s" : ""} ago`;
  }
};

export const isAnythingSelected = (
  object: FormValuesType,
  field: string
): boolean => {
  return Object.entries(object).some(([key, value]) => {
    if (key.startsWith(field)) {
      if (Array.isArray(value) && value.length > 0) {
        return true;
      } else if (typeof value === "string" && value !== "") {
        return true;
      } else if (typeof value === "boolean" && value) {
        return true;
      }
      return false;
    } else {
      return false;
    }
  });
};

export const handleError = (
  error: string | null = "",
  title: string = "Error"
): void => {
  toast.error(title, {
    position: "top-center",
    description: error || "",
    className: "!bg-[#dc2626] !text-white !border-0",
    descriptionClassName: "!text-sm !text-white",
    icon: null,
    duration: Infinity,
    cancelButtonStyle: {
      backgroundColor: "transparent",
      color: "white",
      border: "0",
      fontSize: "14px",
    },
    cancel: {
      label: "Close",
      onClick: () => {},
    },
  });
};

export const handleGenericCatchError = (
  err: any,
  setInputError?: (value: ErrorDetail[]) => void
) => {
  const responseError: ErrorResponse = err?.response?.error;

  if (responseError) {
    handleErrorResponse(responseError, setInputError);
  } else {
    handleError(err.message);
  }

  console.error(err);
};

export const handleErrorResponse = (
  responseError: ErrorResponse,
  setInputError?: (value: ErrorDetail[]) => void
): void => {
  const { error, errors, errorDetails } = responseError;
  if (errorDetails && setInputError) {
    setInputError(errorDetails);
  } else if (error) {
    handleError(error);
  } else if (errors) {
    handleError(errors.join(","));
  } else {
    handleError();
  }
};

export const handleSuccess = (title: string, close: boolean = false): void => {
  toast.success(title, {
    position: "top-right",
    className: "!w-fit dark:!bg-gray-800",
    descriptionClassName: "dark:!text-gray-200 text-nowrap !mx-1.5",
    duration: 2000,
    classNames: {
      icon: "!w-[50px]",
      title: "!max-w-none",
    },
    icon: (
      <div className="sa">
        <div className="sa-success">
          <div className="sa-success-tip"></div>
          <div className="sa-success-long"></div>
          <div className="sa-success-placeholder"></div>
          <div className="sa-success-fix"></div>
        </div>
      </div>
    ),
  });

  if (close) {
    setTimeout(() => {
      handlePopupClose(true);
    }, 2000);
  }
};

export const handlePopupClose = (isSuccess: boolean): void => {
  window.parent.postMessage({ type: "popup-close", isSuccess }, "*");
};

interface ReturnValue {
  value: string;
  label: string | undefined;
}
export const getValueInput = (
  formKey: string,
  inputKey: string,
  formValues: FormValuesType,
  inputValues: InputValuesType | null,
  idOrCodeKey: string
): ReturnValue[] | ReturnValue | undefined => {
  let valueFromForm = formValues[formKey];
  let isTag = false;
  let tagType = "";

  // if the formKey has a dot is a sub input like items.sku
  if (formKey.includes(".")) {
    const fromKeyArray = formKey.split(".");
    valueFromForm =
      formValues[fromKeyArray[0] as string][fromKeyArray[1] as string];
    // check if the input is a tag
    if (["tag", "contactTag", "docTag"].includes(fromKeyArray[1] as string)) {
      isTag = true;
      tagType = fromKeyArray[1]!;
      valueFromForm = formValues[fromKeyArray[0] as string];
    }
  }

  if (!inputValues) return undefined;
  const relevantInputValues = inputValues[inputKey];

  // if there are no input return
  if (!Array.isArray(relevantInputValues)) {
    return undefined;
  }
  // if is a tag get both the included and excluded
  if (isTag) {
    const tagValues = valueFromForm[tagType] ?? [];
    const tagExcludeKey = `${tagType}Exclude`;
    const tagExcludedValues = valueFromForm[tagExcludeKey] ?? [];

    // Create a map to store the original order of items
    const originalOrderMap = new Map();
    relevantInputValues.forEach((item, index) => {
      originalOrderMap.set(item[idOrCodeKey], index);
    });

    // Combine and sort based on the original order
    const allTagValues = [...tagValues, ...tagExcludedValues].sort(
      (a, b) => originalOrderMap.get(a) - originalOrderMap.get(b)
    );

    const mapValueToLabel = (codeOrId: string) => {
      const foundItem = relevantInputValues.find(
        (item) => item[idOrCodeKey] === codeOrId
      );
      return {
        value: codeOrId,
        label: idOrCodeKey === "tagId" ? foundItem?.tagName : foundItem?.name,
      };
    };

    return allTagValues.map(mapValueToLabel);
  }

  // if the value is an array, get the label for each value
  if (Array.isArray(valueFromForm)) {
    return valueFromForm.map((codeOrId) => ({
      value: codeOrId,
      label: relevantInputValues.find(
        (item) => String(item[idOrCodeKey]) === String(codeOrId)
      )?.name,
    }));
  } else if (valueFromForm) {
    let label: string = relevantInputValues.find(
      (item) => item[idOrCodeKey] === valueFromForm
    )?.name;

    return {
      value: valueFromForm,
      label,
    };
  } else {
    return undefined;
  }
};

export const getCurrencySymbol = (isoCode: string | undefined | null) => {
  if (!isoCode) return "";
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: isoCode,
  }); // Extract the currency symbol from the formatted value
  return formatter.formatToParts(12345)[0]?.value;
};

export const getQueryParams = (
  searchParams: URLSearchParams
): {
  [key: string]:
    | string
    | string[]
    | number
    | number[]
    | { [nestedKey: string]: string }
    | boolean;
} => {
  const queryParams: {
    [key: string]:
      | string
      | string[]
      | { [nestedKey: string]: string }
      | boolean;
  } = {};
  searchParams.forEach((value, key) => {
    const regexDate = /^\d{4}-\d{2}-\d{2}$/;
    const [paramName, paramSubName] = key.split(".");

    if (paramName === "token" || paramName === "sr" || paramName === "generate")
      return;

    if (paramSubName === "start" || paramSubName === "end") {
      // Handle date type parameters
      if (!queryParams[paramName!]) {
        queryParams[paramName!] = {};
      }

      if (!regexDate.test(value)) {
        const dynamicKey = `dynamic${
          paramSubName.charAt(0).toUpperCase() + paramSubName.slice(1)
        }ingDate`;
        (queryParams[paramName!] as { [key: string]: string })[dynamicKey] =
          value;
      } else {
        queryParams[key] = value;
      }
    } else {
      // Handle regular parameters

      if (queryParams[key]) {
        if (Array.isArray(queryParams[key])) {
          queryParams[key] = [...(queryParams[key] as string[]), value];
        } else if (typeof queryParams[key] === "object") {
          queryParams[key] = {
            ...(queryParams[key] as { [nestedKey: string]: string }),
            value,
          };
        } else {
          queryParams[key] = [queryParams[key] as string, value];
        }
      } else {
        if (value === "true" || value === "false") {
          queryParams[key] = value === "true";
        } else {
          queryParams[key] = value;
        }
      }
    }
  });

  return queryParams;
};

const isStringAnInteger = (str: string): boolean => {
  const num = Number(str);
  return Number.isInteger(num);
};

const setValue = (
  obj: FormValuesType,
  path: string[],
  value:
    | string[]
    | number[]
    | string
    | number
    | boolean
    | {
        [nestedKey: string]: string;
      }
): void => {
  let current = obj;

  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]!;
    if (!current[key]) current[key] = {};
    current = current[key];
  }
  current[path[path.length - 1]!] = value;
};

export const updateDynamicState = (
  prevState: { [key: string]: string[] | number[] },
  values: {
    [key: string]:
      | string
      | string[]
      | number
      | number[]
      | boolean
      | {
          [nestedKey: string]: string;
        };
  }
): FormValuesType => {
  const updatedState: FormValuesType = { ...prevState };

  for (const key in values) {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      let value = values[key];

      // if value is an empty string skip
      if (value === "") continue;

      // if it contains a comma is an array of value
      if (typeof value === "string" && value.includes(",")) {
        let values: string[] | number[] = value
          .split(",")
          .map((s: string) => s.trim());
        // check if the value is a number if it is transform in integer

        if (isStringAnInteger(values[0]!) && key !== "items.sku") {
          values = values.map(Number);
        }
        setValue(updatedState, [key], values);
      } else {
        if (
          typeof value === "string" &&
          isStringAnInteger(value) &&
          key !== "items.sku"
        ) {
          setValue(updatedState, [key], [Number(value)]);
        } else {
          setValue(updatedState, [key], value!);
        }
      }
    }
  }

  return updatedState;
};

export const transformDict = (
  inputDict: {
    [key: string]:
      | string
      | number
      | string[]
      | number[]
      | boolean
      | { [nestedKey: string]: string };
  },
  isQuery: boolean = false
): ItemsListData => {
  const outputDict: { query: { [key: string]: any } } = { query: {} };

  for (const [key, value] of Object.entries(inputDict)) {
    const newKey = key.replace("query.", "");

    if (typeof value === "string" && value.length > 0) {
      if (!value.includes(",")) {
        if (!isNaN(Number(value))) {
          outputDict.query[newKey] = [Number(value)];
        } else if (value === "true" || value === "false") {
          outputDict.query[newKey] = value === "true";
        } else {
          outputDict.query[newKey] = value;
        }
      } else if (value.split(",").every((v) => !isNaN(Number(v)))) {
        outputDict.query[newKey] = value.split(",").map(Number);
      } else if (value.includes(",")) {
        outputDict.query[newKey] = value.split(",");
      } else {
        outputDict.query[newKey] = value;
      }
    } else {
      outputDict.query[newKey] = value;
    }
  }

  return isQuery ? outputDict : outputDict.query;
};

export const transformToConfigurationParameters = (
  input: any
): ConfigurationParameter[] => {
  const result: ConfigurationParameter[] = [];

  const traverseObject = (obj: any, parentKey: string = "") => {
    for (const key in obj) {
      const value = obj[key];
      const fullKey = parentKey ? `${parentKey}.${key}` : key;

      if (Array.isArray(value)) {
        const isArrayOfObjects = value.every(
          (item) =>
            typeof item === "object" && item !== null && !Array.isArray(item)
        );

        if (isArrayOfObjects) {
          const stringifyValue = value
            .map((item) => JSON.stringify({ [item.key]: item.value }))
            .join(",")
            .replace(/\\/g, "");

          result.push({
            name: key,
            value: stringifyValue,
          });
        } else {
          result.push({
            name: fullKey,
            value: value.length > 0 ? value.join(",") : null,
          });
        }
      } else if (
        typeof value === "object" &&
        value !== null &&
        !(value instanceof Date)
      ) {
        traverseObject(value, fullKey);
      } else {
        result.push({
          name: fullKey,
          value: value !== undefined ? String(value) : null,
        });
      }
    }
  };

  traverseObject(input);

  return result;
};

export const extractDefaultValues = (
  parameters: ConfigurationProperty[] | null | undefined
): FormValuesType => {
  const initialValues: FormValuesType = {};

  parameters?.forEach(({ type, name, defaultValue }) => {
    // if param type is Date range
    if (type === "DATE_RANGE") {
      if (!defaultValue) {
        initialValues[name!] = {
          start: null,
          end: null,
        };
        return;
      }
      // check if if the string contains number so is a not dynamic date just transform in object
      const obj = JSON.parse(defaultValue);
      if (/\d/.test(defaultValue)) {
        initialValues[name!] = obj;
      } else {
        // if it does not contain number then is a dynamic date so change the keys
        initialValues[name!] = {
          dynamicStartingDate: obj.start,
          dynamicEndingDate: obj.end,
        };
      }
    } else if (type === "CHECKBOX" && defaultValue !== null) {
      initialValues[name!] = defaultValue === "true";
    } else if (defaultValue !== null) {
      initialValues[name!] = defaultValue;
    }
  });
  return initialValues;
};

export const formatActivityPipelineDate = (
  dateString: Date | undefined | string | null
): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const userTimezoneOffsetName =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const userTimezoneOffset = getTimezoneOffset(userTimezoneOffsetName);

  if (userTimezoneOffset !== undefined) {
    const utcTime = date.getTime() + date.getTimezoneOffset() * 60000;
    date.setTime(utcTime + userTimezoneOffset * 60000);
  }

  const year = date.getFullYear().toString().slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const day = ("0" + date.getDate()).slice(-2);
  const hours = ("0" + date.getHours()).slice(-2);
  const minutes = ("0" + date.getMinutes()).slice(-2);
  const seconds = ("0" + date.getSeconds()).slice(-2);

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const getTimezoneOffset = (timezone: string): number | undefined => {
  const now = new Date();
  const timezoneOffset = now.toLocaleString("en", {
    timeZone: timezone,
    timeZoneName: "short",
  });
  const offsetParts = timezoneOffset.split(" ");
  if (
    offsetParts.length === 2 &&
    (offsetParts[0] === "GMT" || offsetParts[0] === "UTC")
  ) {
    const sign = offsetParts[1]![0] === "+" ? 1 : -1;
    const hours = parseInt(offsetParts[1]!.substring(1, 3), 10);
    const minutes = parseInt(offsetParts[1]!.substring(4, 6), 10);
    return (hours * 60 + minutes) * sign;
  }
  return undefined;
};

export const getColorClasses = (
  page: string | undefined
): { textColor: string; bgColor: string } => {
  let textColor = "";
  let bgColor = "";
  if (page === "customer") {
    textColor = "text-sky-600 dark:text-sky-300";
    bgColor = "bg-sky-100 dark:bg-sky-600";
  } else if (page === "vendor") {
    textColor = "text-purple-600 dark:text-purple-300";
    bgColor = "bg-purple-300 dark:bg-purple-600";
  } else if (page === "inventory") {
    textColor = "text-green-600 dark:text-green-300";
    bgColor = "bg-green-100 dark:bg-green-600";
  }
  return { textColor, bgColor };
};

export const handleIdString = (id: string | number | undefined): string => {
  if (!id) return "";
  return id.toString().padStart(6, "0");
};

export const containsOnlyNumbers = (str: string): boolean => {
  const regex = /^[0-9]+$/;
  return regex.test(str);
};

const isNumberEntered = (
  { key, keyCode, ctrlKey, metaKey }: KeyboardEvent<HTMLInputElement>,
  allowExclamation: boolean = false,
  allowComparison: boolean = false
): boolean => {
  if (
    key === "." ||
    key === "Backspace" ||
    key === "Delete" ||
    key === "ArrowLeft" ||
    key === "ArrowRight" ||
    key === "Tab" ||
    key === "Enter" ||
    key === "Escape" ||
    key === "Home" ||
    key === "End" ||
    key === "ArrowUp" ||
    key === "ArrowDown"
  )
    return true;

  if (
    // Allow: Ctrl+A,Ctrl+C,Ctrl+V, Command+A
    (keyCode === 65 || keyCode === 86 || keyCode === 67) &&
    (ctrlKey === true || metaKey === true)
  ) {
    // let it happen, don't do anything

    return true;
  }

  if (allowExclamation && key === "!") return true;

  if (allowComparison && (key === "<" || key === ">" || key === "-"))
    return true;

  return /^\d$/.test(key);
};

export const allowOnlyNumbers = (
  e: KeyboardEvent<HTMLInputElement>,
  allowComparison: boolean = false
): void => {
  if (!isNumberEntered(e, false, allowComparison)) {
    e.preventDefault();
  }
};

export const handleIntegerFiltering = (
  e: ChangeEvent<HTMLInputElement>,
  allowComparison: boolean = false
): number | undefined => {
  let value = e.target.value.replaceAll(",", "");

  if (value.endsWith(".") && value.split(".").length > 2) {
    return value.slice(0, -1) as unknown as number;
  }

  if (isNaN(parseInt(value))) {
    const regex = allowComparison ? /[^\d.<>-]/g : /[^\d.]/g;
    value = value.replace(regex, "");

    if (value === "") {
      return undefined;
    }
    return value as unknown as number;
  }

  if (!value) {
    return;
  } else {
    const regex = allowComparison ? /[^\d.<>-]/g : /[^\d.]/g;
    return value.replace(regex, "") as unknown as number;
  }
};

export const discountValueOnly = (e: KeyboardEvent<HTMLInputElement>): void => {
  if (!isNumberEntered(e, true)) {
    e.preventDefault();
  }
};

export const transformObjectToArray = (
  obj: Record<string, any> | null
): { name: string; value: any }[] => {
  if (!obj) return [];
  return Object.entries(obj).flatMap(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (typeof value === "string" && value.trim() === "") {
        return [];
      }
      if (Array.isArray(value)) {
        return { name: key, value: value.join(",") };
      } else {
        if (typeof value === "number" || typeof value === "boolean") {
          return { name: key, value: value.toString() };
        }
        return { name: key, value };
      }
    } else {
      return [];
    }
  });
};

export const transformArrayToObject = (
  array: ConfigurationParameter[] | null | undefined
): { [key: string]: string | null } => {
  if (!array) return {};
  return array.reduce(
    (acc, item) => {
      if (item.name) {
        acc[item.name] = item.value ?? null;
      }
      return acc;
    },
    {} as { [key: string]: string | null }
  );
};

export const delayedState = (
  state: boolean,
  setViewState: Dispatch<React.SetStateAction<ViewOptionsType>>,
  timeoutRef: MutableRefObject<NodeJS.Timeout | null>
): (() => void) => {
  if (state) {
    timeoutRef.current = setTimeout(() => {
      setViewState((prev) => ({ ...prev, overflowDelayed: true }));
      timeoutRef.current = null;
    }, 500);
  } else {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setViewState((prev) => ({ ...prev, overflowDelayed: false }));
  }

  return () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };
};

export const scrollToTop = (): void => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

export const scrollToBottom = (): void => {
  window.scrollTo({
    top: document.body.scrollHeight,
    behavior: "smooth",
  });
};

export const addParameter = (
  parameters: ConfigurationParameter[],
  name: string,
  value: any
): void => {
  if (Array.isArray(value) && value.length > 0) {
    parameters.push({ name, value: value.join(",") });
  } else if (typeof value === "string" && value.trim() !== "") {
    if (name.split(".")[1] === "dynamicStartingDate") {
      name = `${name.split(".")[0]}.start`;
    } else if (name.split(".")[1] === "dynamicEndingDate") {
      name = `${name.split(".")[0]}.end`;
    }

    parameters.push({ name, value: value.trim().replace(/\s+/g, ",") });
  } else if (typeof value === "boolean" && value) {
    parameters.push({ name, value: value.toString() });
  }
};

export const getSortedValues = (
  values: valueSortedType,
  valuesExclude: valueSortedType,
  options: ValueLabelStringType[]
): (string | number)[] => {
  const checkArray = (value: valueSortedType) =>
    Array.isArray(value)
      ? value
      : value == null || value === undefined
        ? []
        : [value];

  const optionOrder = options.map((opt) => opt.value);

  const sortedValues = [
    ...checkArray(values),
    ...checkArray(valuesExclude),
  ].sort((a, b) => {
    const indexA = optionOrder.indexOf(a);
    const indexB = optionOrder.indexOf(b);

    return (
      (indexA !== -1 ? indexA : Infinity) - (indexB !== -1 ? indexB : Infinity)
    );
  });

  return sortedValues;
};

export const formatLabelVendor = ({
  uom,
  price,
  contact,
}: ItemsMappingItemVendorResponse): JSX.Element => {
  return (
    <div className="-mt-1">
      <p className="text-sm">{contact?.codeName}</p>
      <p className="text-[10px] leading-[1] text-gray-600">
        [
        {asMoney({
          amount: price?.each || undefined,
          currencyCode: price?.currency,
        })}
        {uom?.name ? `@${uom.name}` : ""}]
      </p>
    </div>
  );
};

export function handleInputChange(
  setFormValues: undefined | ((value: FormValuesType) => void),
  navigate: NavigateFunction | undefined,
  key: string,
  section: string | null | undefined,
  enteredValue: ValueInputChangeType | ValueInputChangeType[] | null,
  valueAsArray: boolean | undefined,
  dictionaryKey?: string | null
) {
  if (dictionaryKey) {
    key = `${key}.${dictionaryKey}`;
  }

  const finalKey = section ? `${section}.${key}` : key;

  const stringValue = (
    value: ValueInputChangeType | ValueInputChangeType[] | null
  ): string | number | (string | number)[] | null => {
    if (value === null || value === undefined) return null;

    const singularValue = (value: ValueInputChangeType): string | number => {
      if (value === null || value === undefined) return "";
      if (typeof value === "string" && value.trim() === "") return "";
      if (typeof value === "number" && !isNaN(value)) return value;
      if (typeof value === "boolean") return value ? "true" : "false";

      if (typeof value === "object" && "value" in value) {
        return value.value.toString().trim().replace(/\s+/g, ",");
      }
      if (value instanceof Date) {
        return value.toISOString().split("T")[0]; // Format as YYYY-MM-DD
      }
      return value.toString().trim().replace(/\s+/g, ",");
    };

    if (valueAsArray) {
      if (!Array.isArray(value)) {
        return [singularValue(value)];
      }
      return value.map(singularValue);
    } else if (Array.isArray(value)) {
      return value.map(singularValue).join(",");
    } else {
      return singularValue(value);
    }
  };

  const value = stringValue(enteredValue);

  if (setFormValues) {
    setFormValues((prev: FormValuesType) => {
      let val = value;
      if (dictionaryKey) {
        const mappingName = key.split(".")[0];
        const mappingKey = key.split(".")[1];
        return {
          ...prev,
          [section || mappingName]: section
            ? {
                ...prev[section],
                [mappingName]: {
                  ...(prev[section]?.[mappingName] || {}),
                  [mappingKey]: val,
                },
              }
            : {
                ...(prev[mappingName] || {}),
                [mappingKey]: val,
              },
        };
      }
      if (typeof enteredValue === "boolean" && enteredValue == false) {
        // If the entered value is false, remove the key from the state
        if (section) {
          const { [key]: _, ...rest } = prev[section];
          return { ...prev, [section]: rest };
        } else {
          const { [key]: _, ...rest } = prev;
          return rest;
        }
      }
      return {
        ...prev,
        [section || key]: section ? { ...prev[section], [key]: val } : val,
      };
    });
  }

  if (navigate) {
    const searchParams = new URLSearchParams(window.location.search);
    const queryValue = value && Array.isArray(value) ? value.join(",") : value;

    if (typeof enteredValue === "boolean" && enteredValue == false) {
      searchParams.delete(finalKey);
    } else if (
      queryValue === null ||
      queryValue === undefined ||
      queryValue === ""
    ) {
      searchParams.delete(finalKey);
    } else {
      searchParams.set(finalKey, queryValue.toString());
    }

    const newUrl = `${window.location.pathname}?${searchParams
      .toString()
      .replace(/%2C/g, ",")}`;
    navigate(newUrl);
  }
}

export const getKeysFromObject = (obj: Object) => {
  return Object.keys(obj).map((key) => ({ name: key }));
};

export const checkErrorsFieldExists = (
  errors: ErrorDetail[],
  parameters: ConfigurationProperty[] | null | undefined,
  obj: Record<string, any>
): boolean => {
  const fieldExists = (field: string) => {
    return (
      (parameters && parameters.some(({ name }) => name === field)) ||
      obj.hasOwnProperty(field)
    );
  };

  return errors?.every(({ field }) => fieldExists(field!));
};

export const convertImageToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
};

export const checkSyncErrorsFieldExists = (
  errorDetails: ErrorDetail[],
  parameters: ConfigurationProperty[] | null | undefined,
  syncConfigurations: Record<SyncType, FormValuesType>
): ErrorDetail[] => {
  let syncErrors: ErrorDetail[] = [];
  const fieldExists = (field: string, syncType: SyncType) => {
    const syncConfig = syncConfigurations[syncType];
    return (
      (parameters && parameters.some(({ name }) => name === field)) ||
      syncConfig.hasOwnProperty(field)
    );
  };

  Object.keys(syncConfigurations).forEach((syncType) => {
    errorDetails.forEach((errorDetail) => {
      const field = errorDetail.field!;

      if (!fieldExists(field, syncType as SyncType)) {
        syncErrors.push(errorDetail);
      }
    });
  });

  return syncErrors;
};

export const getListOptions = async (
  inputValues: InputValuesType | null,
  input: ConfigurationProperty
): Promise<ValueLabelStringType[] | NestedOptionType[]> => {
  let options: ValueLabelStringType[] | NestedOptionType[] = [];

  switch (input.type) {
    case "WAREHOUSE":
      options =
        inputValues?.warehouses?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];
      break;

    case "LIST_NAME": {
      const { data, error } = await AdministrationService.listResponse({
        path: { listType: input.listName! },
      });

      if (error) {
        handleErrorResponse(error);
        options = [];
      } else {
        options =
          data?.results?.map(({ id, name }) => ({
            value: id,
            label: name,
          })) || [];
      }
      break;
    }

    case "WAREHOUSE_GROUP":
      options =
        inputValues?.warehouseGroups?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];
      break;

    case "MARKETPLACE_AMAZON": {
      const { data } = await AppIntegrationService.marketplacesListAll();
      options =
        data?.results
          ?.filter((item) => item.type?.toLowerCase() == "amazon")
          .map(({ marketplaceId, name }) => ({
            value: marketplaceId,
            label: name!,
          })) || [];

      break;
    }

    case "WAREHOUSE_WITH_GROUPS":
      const warehouseOptions =
        inputValues?.warehouses?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];

      const warehouseGroupOptions =
        inputValues?.warehouseGroups?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];

      options = [
        {
          options: warehouseOptions,
        },
        {
          label: "Warehouse Groups",
          options: warehouseGroupOptions,
        },
      ];
      break;

    case "SALES_REP":
      options =
        inputValues?.salesReps?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];
      break;

    case "LIST":
      options = input.options
        ? Object.keys(input.options).map((key) => ({
            value: key,
            label: input.options![key]!,
          }))
        : [];
      break;

    case "DOC_TAG":
      options =
        inputValues?.docTags?.map(({ tagId, tagName }) => ({
          value: tagId,
          label: tagName!,
        })) || [];
      break;

    case "UOM":
      options =
        inputValues?.itemUoms?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];
      break;

    case "ITEM_TAG":
      options =
        inputValues?.itemTags?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];
      break;

    case "ITEM_CATEGORY":
      options =
        inputValues?.itemCategories?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];
      break;

    case "ITEM_BRAND":
      options =
        inputValues?.itemBrands?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];
      break;

    case "ITEM_PRICELIST":
      options =
        inputValues?.pricelists?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];
      break;

    case "PRINT_QUEUE":
      options =
        inputValues?.printQueues?.map(({ id, name }) => ({
          value: id,
          label: name,
        })) || [];
      break;

    case "CURRENCY":
      options =
        inputValues?.currencies?.map(({ id }) => ({
          value: id,
          label: id,
        })) || [];
      break;

    default:
      break;
  }

  return options;
};

export const isListOptionForInput = (
  inputType: string | null | undefined
): boolean => {
  return (
    inputType === "WAREHOUSE" ||
    inputType === "WAREHOUSE_WITH_GROUPS" ||
    inputType === "WAREHOUSE_GROUP" ||
    inputType === "LIST" ||
    inputType === "SALES_REP" ||
    inputType === "DOC_QUEUE" ||
    inputType === "ITEM_TAG" ||
    inputType === "ITEM_CATEGORY" ||
    inputType === "ITEM_PRICELIST" ||
    inputType === "ITEM_BRAND" ||
    inputType === "UOM" ||
    inputType === "DOC_TAG" ||
    inputType === "PRINT_QUEUE" ||
    inputType === "CUSTOM_FEED" ||
    inputType === "MARKETPLACE_AMAZON" ||
    inputType === "CURRENCY" ||
    inputType === "LIST_NAME"
  );
};

export const getPipelineConf = (
  direction?: string,
  type?: string | null,
  config?: PipelinesConfigurationResponse
): PipelineConfiguration | undefined => {
  const dir = direction?.toUpperCase();
  const typeUppercase = type?.toUpperCase();

  if (!dir || !typeUppercase || !config) return;

  if (dir === "INBOUND") {
    if (typeUppercase === "ORDERS") return config.inbound?.[`orders`];
    if (typeUppercase === "INVENTORY") return config.inbound?.[`inventory`];
  }

  if (dir === "OUTBOUND") {
    if (typeUppercase === "INVENTORY") return config.outbound?.[`inventory`];
    if (typeUppercase === "SHIPMENTS") return config.outbound?.[`shipments`];
    if (typeUppercase === "FEED") return config.outbound?.[`feed`];
  }

  return;
};

export const customSorting = (
  rowA: any,
  rowB: any,
  columnId: string
): number => {
  if (columnId.startsWith("M")) {
    columnId = `periodResults.${columnId}.qty`;
  }
  const valueA = rowA.original[`${columnId}`];
  const valueB = rowB.original[`${columnId}`];

  if (valueA === valueB) {
    return 0;
  }

  return valueA - valueB;
};

export const capitalizeWord = (str: string | undefined | null): string => {
  if (!str) return "";
  if (str === "NA") return "N/A";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const humanizeString = (string: string | undefined): string => {
  if (!string) return "";
  string = string
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  string = string.charAt(0).toUpperCase() + string.slice(1);

  return string;
};

export const humanizeStringCapitalized = (
  string: string | undefined
): string => {
  if (!string) return "";
  string = string
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

  // Capitalize the first letter of each word
  string = string
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return string;
};

export const hourOptions = Array.from({ length: 24 }, (_, index) => ({
  value: `${index + 1}`,
  label: `${index + 1}`,
}));

export const handleColumnRounded = (
  index: number,
  lastIndex: number
): string => {
  if (index === 0) return "rounded-s-md";
  if (index === lastIndex - 1) return "rounded-e-md";
  return "";
};

export const stringifyArray = (
  array: IdName[] | StringIdName[] | null | undefined,
  maxLength: number | undefined = undefined
): string => {
  if (!array || !Array.isArray(array)) return "";

  if (maxLength && array.length > maxLength) {
    return `${array
      .slice(0, maxLength)
      .map((tag) => tag.name)
      .join(", ")}...`;
  }

  return array?.length
    ? array
        .map((tag) => tag.name)
        .filter((name) => name)
        .join(", ")
    : "";
};

export const getColorStyleClasses = (theme: string) => {
  switch (theme) {
    case "blue":
      return {
        gradientFrom: "from-blue-50",
        gradientTo: "to-blue-100",
        border: "border-blue-200",
        iconBg: "bg-blue-500",
        buttonBg: "bg-blue-600",
        buttonHover: "hover:bg-blue-700",
        focusRing: "focus:ring-blue-500",
        textColor: "text-blue-800",
        subtitleColor: "text-blue-700",
      };
    case "green":
      return {
        gradientFrom: "from-green-50",
        gradientTo: "to-green-100",
        border: "border-green-200",
        iconBg: "bg-green-500",
        buttonBg: "bg-green-600",
        buttonHover: "hover:bg-green-700",
        focusRing: "focus:ring-green-500",
        textColor: "text-green-800",
        subtitleColor: "text-green-700",
      };
    case "red":
      return {
        gradientFrom: "from-red-50",
        gradientTo: "to-red-100",
        border: "border-red-200",
        iconBg: "bg-red-500",
        buttonBg: "bg-red-600",
        buttonHover: "hover:bg-red-700",
        focusRing: "focus:ring-red-500",
        textColor: "text-red-800",
        subtitleColor: "text-red-700",
      };
    case "purple":
      return {
        gradientFrom: "from-purple-50",
        gradientTo: "to-purple-100",
        border: "border-purple-200",
        iconBg: "bg-purple-500",
        buttonBg: "bg-purple-600",
        buttonHover: "hover:bg-purple-700",
        focusRing: "focus:ring-purple-500",
        textColor: "text-purple-800",
        subtitleColor: "text-purple-700",
      };
    case "yellow":
      return {
        gradientFrom: "from-yellow-50",
        gradientTo: "to-yellow-100",
        border: "border-yellow-200",
        iconBg: "bg-yellow-500",
        buttonBg: "bg-yellow-600",
        buttonHover: "hover:bg-yellow-700",
        focusRing: "focus:ring-yellow-500",
        textColor: "text-yellow-800",
        subtitleColor: "text-yellow-700",
      };
    case "indigo":
      return {
        gradientFrom: "from-indigo-50",
        gradientTo: "to-indigo-100",
        border: "border-indigo-200",
        iconBg: "bg-indigo-500",
        buttonBg: "bg-indigo-600",
        buttonHover: "hover:bg-indigo-700",
        focusRing: "focus:ring-indigo-500",
        textColor: "text-indigo-800",
        subtitleColor: "text-indigo-700",
      };
    case "gray":
      return {
        gradientFrom: "from-gray-100",
        gradientTo: "to-gray-200",
        border: "border-gray-200",
        iconBg: "bg-gray-500",
        buttonBg: "bg-green-600",
        buttonHover: "hover:bg-gray-700",
        focusRing: "focus:ring-gray-500",
        textColor: "text-gray-700",
        subtitleColor: "text-gray-600",
      };
    default:
      return {
        gradientFrom: "from-blue-50",
        gradientTo: "to-blue-100",
        border: "border-blue-200",
        iconBg: "bg-blue-500",
        buttonBg: "bg-blue-600",
        buttonHover: "hover:bg-blue-700",
        focusRing: "focus:ring-blue-500",
        textColor: "text-blue-800",
        subtitleColor: "text-blue-700",
      };
  }
};

export const formValuesDefaults: FormValuesType = {
  "items.appCategory": [],
  "items.appCategoryExclude": false,
  "items.appHidden": undefined,
  "items.appMarkedNew": undefined,
  "items.AutoConfirm": false,
  "items.brand": [],
  "items.brandExclude": false,
  "items.category": [],
  "items.categoryExclude": false,
  "items.department": [],
  "items.departmentExclude": false,
  "items.eol": undefined,
  "items.IncChildItem": false,
  "items.Purchasable": false,
  "items.Sellable": false,
  "items.sku": [],
  "items.skuExclude": false,
  "items.tag": [],
  "items.tagExclude": [],
  "items.tagsAll": false,
  "items.type": [],
  "items.vendor": [],
  "items.vendorExclude": false,

  "sales.contact": [],
  "sales.contactExclude": false,
  "sales.docTag": [],
  "sales.docTagExclude": [],
  "sales.contactTag": [],
  "sales.contactTagExclude": [],
  "sales.salesRep": [],
  "sales.salesRepExclude": false,

  /*    sales: {
    contact: [],
    contactExclude: false,
    docTag: [],
    docTagExclude: [],
    contactTag: [],
    contactTagExclude: [],
    salesRep: [],
    salesRepExclude: false,
  },
 items: {
    sku: "",
    skuExclude: false,
    brand: [],
    brandExclude: false,
    category: [],
    categoryExclude: false,
    department: [],
    departmentExclude: false,
    appCategory: [],
    appCategoryExclude: false,
    tag: [],
    tagExclude: [],
    vendor: [],
    vendorExclude: false,
    type: [],
    eol: undefined,
    appMarkedNew: undefined,
    appHidden: undefined,
  }, */
};

export const salesSearchDefaults: any = {
  query: {
    // general
    warehouse: undefined,
    contact: undefined,
    status: undefined,
    packingStage: undefined,
    invoiceStage: undefined,
    finalized: undefined,
    creditTerms: undefined,
    "contact.exclude": false,
    stage: [],
    "stage.exclude": false,
    resendMode: undefined,

    // by Item
    "items.sku": [],
    "items.skuExclude": false,
    "items.brand": [],
    "items.brandExclude": false,
    "items.category": [],
    "items.categoryExclude": false,
    "items.department": [],
    "items.departmentExclude": false,
    "items.appCategory": [],
    "items.appCategoryExclude": false,
    "items.tag": [],
    "items.tagsAll": false,
    "items.tagExclude": [],
    "items.vendor": [],
    "items.vendorExclude": false,
    "items.type": [],
    "items.eol": undefined,
    "items.appMarkedNew": undefined,
    "items.appHidden": undefined,

    // TODO

    //dispatch input
    "dispatchDate.start": undefined,
    "dispatchDate.end": undefined,
    assignedCarrier: undefined,
    carrierLabelMode: undefined,
    pckId: undefined,
    resultsSorting: undefined,
    linesMode: [],
    // TODO
    excludeLinesMode: [],

    // Advanced
    "date.start": undefined,
    "date.end": undefined,
    "dueDateRange.start": undefined,
    "dueDateRange.end": undefined,
    id: undefined,
    channels: undefined,
    shipmentStatus: undefined,
    backOrder: undefined,
    salesRepId: undefined,
    parked: undefined,
    printed: undefined,
    currency: undefined,
    dueOnly: undefined,

    // TODO
    tag: [],
    tagExclude: [],
    consignment: undefined,
    reference: undefined,
    dispatchMissing: false,
    mpId: undefined,
    packingReady: undefined,
    paymentStatus: [],
    itemState: undefined,
  },
};

export const dateSelectOption: ValueLabelStringType[] = [
  /*{ value: "", label: "Select an option" }, */
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "start-of-week", label: "Start of week" },
  { value: "end-of-week", label: "End of week" },
  { value: "start-of-month", label: "Start of month" },
  { value: "end-of-month", label: "End of month" },
  { value: "start-of-year", label: "Start of year" },
  { value: "end-of-year", label: "End of year" },
  { value: "previous-start-of-month", label: "Start of prev month" },
  { value: "previous-end-of-month", label: "End of prev month" },
  { value: "previous-start-of-year", label: "Start of prev year" },
  { value: "previous-end-of-year", label: "End of prev year" },
];

export const minuteOptions: ValueLabelStringType[] = [
  { value: "00", label: "00" },
  { value: "15", label: "15" },
  { value: "30", label: "30" },
  { value: "45", label: "45" },
];

export const monthOptions: ValueLabelStringType[] = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

export const itemTypeOption: { [key: string]: string } = {
  STOCK_ITEM: "Stock Item",
  SERVICE_ITEM: "Service Item",
  KIT_ITEM: "Kit Item",
  BOM_ITEM: "Bom Item",
  GROUP_ITEM: "Group Item",
};

export const drillDownTypes: {
  [key: string]:
    | "INBOUND"
    | "OUTBOUND"
    | "AGING"
    | "UNINVOICED"
    | "TRN"
    | "POB2B"
    | "PODD"
    | "BLOCKED";
} = {
  onOrder: "INBOUND",
  reserved: "OUTBOUND",
  aging: "AGING",
  uninvoiced: "UNINVOICED",
  inTransit: "TRN",
  backToBack: "POB2B",
  podd: "PODD", //todo
  blocked: "BLOCKED",
};

export const inventoryTypeOptions: { [key: string]: string } = {
  onHand: "OH",
  available: "AV",
  onOrder: "IN",
  inTransit: "TRN",
};

export const periodTypeOptions: { [key: string]: string } = {
  openingStock: "O",
  closingStock: "C",
  daysOtOfStock: "OOS",
  inTransit: "TRN",
};

export const periodTypeOptionsName: { [key: string]: string } = {
  qty: "Quantity",
  promoQty: "Promo quantity",
  openingStock: "Opening stock",
  closingStock: "Closing stock",
  daysOtOfStock: "Days out Of Stock",
  inTransit: "In transport",
};

export const linkTypes: { [key: string]: string } = {
  RCT: "/vendor/rct",
  RFQ: "/vendor/rfq",
  BLL: "/vendor/bll",
  SQ: "/customer/sq",
  INV: "/customer/inv",
  SHP: "/customer/shp",
  TRN: "/inventory/trn",
  TRC: "/inventory/trc",
  ASM: "/inventory/asm",
  MSG: "/edi/message",
};

export const dayOfWeekArray = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
  { value: "0", label: "Sunday" },
];

export const selectStylesTable: StylesConfig<
  string,
  boolean,
  GroupBase<string>
> = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    height: "32px",
    minHeight: "32px",
    alignItems: "start",
    boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);",
  }),
  container: (base) => ({
    ...base,
    width: "110px",
    height: "32px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: "0px",
  }),
  clearIndicator: (base) => ({
    ...base,
    paddingRight: "1px",
    paddingLeft: "0px",
    paddingTop: "5px",
    ":hover": {
      backgroundColor: "transparent",
      color: "red",
    },
    "dark:": {
      backgroundColor: "yellow",
      color: "black",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: "5px",
    height: "32px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    paddingRight: "1px",
    paddingLeft: "1px",
    paddingTop: "5px",
  }),
  singleValue: (base) => ({
    ...base,
    marginLeft: "4px",
  }),
  input: (base) => ({
    ...base,
    paddingTop: "0px",
    marginBottom: "0px",
  }),
};

export const timeZones = [
  { value: undefined, label: "System default" },
  {
    value: "Dateline Standard Time",
    label: "(UTC-12:00) International Date Line West",
  },
  { value: "UTC-11", label: "(UTC-11:00) Coordinated Universal Time-11" },
  { value: "Aleutian Standard Time", label: "(UTC-10:00) Aleutian Islands" },
  { value: "Hawaiian Standard Time", label: "(UTC-10:00) Hawaii" },
  { value: "Marquesas Standard Time", label: "(UTC-09:30) Marquesas Islands" },
  { value: "Alaskan Standard Time", label: "(UTC-09:00) Alaska" },
  { value: "UTC-09", label: "(UTC-09:00) Coordinated Universal Time-09" },
  {
    value: "Pacific Standard Time (Mexico)",
    label: "(UTC-08:00) Baja California",
  },
  { value: "UTC-08", label: "(UTC-08:00) Coordinated Universal Time-08" },
  {
    value: "Pacific Standard Time",
    label: "(UTC-08:00) Pacific Time (US & Canada)",
  },
  { value: "US Mountain Standard Time", label: "(UTC-07:00) Arizona" },
  {
    value: "Mountain Standard Time (Mexico)",
    label: "(UTC-07:00) La Paz, Mazatlan",
  },
  {
    value: "Mountain Standard Time",
    label: "(UTC-07:00) Mountain Time (US & Canada)",
  },
  { value: "Yukon Standard Time", label: "(UTC-07:00) Yukon" },
  {
    value: "Central America Standard Time",
    label: "(UTC-06:00) Central America",
  },
  {
    value: "Central Standard Time",
    label: "(UTC-06:00) Central Time (US & Canada)",
  },
  { value: "Easter Island Standard Time", label: "(UTC-06:00) Easter Island" },
  {
    value: "Central Standard Time (Mexico)",
    label: "(UTC-06:00) Guadalajara, Mexico City, Monterrey",
  },
  { value: "Canada Central Standard Time", label: "(UTC-06:00) Saskatchewan" },
  {
    value: "SA Pacific Standard Time",
    label: "(UTC-05:00) Bogota, Lima, Quito, Rio Branco",
  },
  { value: "Eastern Standard Time (Mexico)", label: "(UTC-05:00) Chetumal" },
  {
    value: "Eastern Standard Time",
    label: "(UTC-05:00) Eastern Time (US & Canada)",
  },
  { value: "Haiti Standard Time", label: "(UTC-05:00) Haiti" },
  { value: "Cuba Standard Time", label: "(UTC-05:00) Havana" },
  { value: "US Eastern Standard Time", label: "(UTC-05:00) Indiana (East)" },
  {
    value: "Turks And Caicos Standard Time",
    label: "(UTC-05:00) Turks and Caicos",
  },
  { value: "Paraguay Standard Time", label: "(UTC-04:00) Asuncion" },
  {
    value: "Atlantic Standard Time",
    label: "(UTC-04:00) Atlantic Time (Canada)",
  },
  { value: "Venezuela Standard Time", label: "(UTC-04:00) Caracas" },
  { value: "Central Brazilian Standard Time", label: "(UTC-04:00) Cuiaba" },
  {
    value: "SA Western Standard Time",
    label: "(UTC-04:00) Georgetown, La Paz, Manaus, San Juan",
  },
  { value: "Pacific SA Standard Time", label: "(UTC-04:00) Santiago" },
  { value: "Newfoundland Standard Time", label: "(UTC-03:30) Newfoundland" },
  { value: "Tocantins Standard Time", label: "(UTC-03:00) Araguaina" },
  { value: "E. South America Standard Time", label: "(UTC-03:00) Brasilia" },
  {
    value: "SA Eastern Standard Time",
    label: "(UTC-03:00) Cayenne, Fortaleza",
  },
  {
    value: "Argentina Standard Time",
    label: "(UTC-03:00) City of Buenos Aires",
  },
  { value: "Montevideo Standard Time", label: "(UTC-03:00) Montevideo" },
  { value: "Magallanes Standard Time", label: "(UTC-03:00) Punta Arenas" },
  {
    value: "Saint Pierre Standard Time",
    label: "(UTC-03:00) Saint Pierre and Miquelon",
  },
  { value: "Bahia Standard Time", label: "(UTC-03:00) Salvador" },
  { value: "UTC-02", label: "(UTC-02:00) Coordinated Universal Time-02" },
  { value: "Greenland Standard Time", label: "(UTC-02:00) Greenland" },
  {
    value: "Mid-Atlantic Standard Time",
    label: "(UTC-02:00) Mid-Atlantic - Old",
  },
  { value: "Azores Standard Time", label: "(UTC-01:00) Azores" },
  { value: "Cape Verde Standard Time", label: "(UTC-01:00) Cabo Verde Is." },
  { value: "UTC", label: "(UTC) Coordinated Universal Time" },
  {
    value: "GMT Standard Time",
    label: "(UTC+00:00) Dublin, Edinburgh, Lisbon, London",
  },
  {
    value: "Greenwich Standard Time",
    label: "(UTC+00:00) Monrovia, Reykjavik",
  },
  { value: "Sao Tome Standard Time", label: "(UTC+00:00) Sao Tome" },
  { value: "Morocco Standard Time", label: "(UTC+01:00) Casablanca" },
  {
    value: "W. Europe Standard Time",
    label: "(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna",
  },
  {
    value: "Central Europe Standard Time",
    label: "(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague",
  },
  {
    value: "Romance Standard Time",
    label: "(UTC+01:00) Brussels, Copenhagen, Madrid, Paris",
  },
  {
    value: "Central European Standard Time",
    label: "(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb",
  },
  {
    value: "W. Central Africa Standard Time",
    label: "(UTC+01:00) West Central Africa",
  },
  { value: "GTB Standard Time", label: "(UTC+02:00) Athens, Bucharest" },
  { value: "Middle East Standard Time", label: "(UTC+02:00) Beirut" },
  { value: "Egypt Standard Time", label: "(UTC+02:00) Cairo" },
  { value: "Israel Standard Time", label: "(UTC+02:00) Jerusalem" },
  { value: "Arab Standard Time", label: "(UTC+03:00) Kuwait, Riyadh" },
  {
    value: "Russia Time Zone 11",
    label: "(UTC+12:00) Anadyr, Petropavlovsk-Kamchatsky",
  },
  {
    value: "New Zealand Standard Time",
    label: "(UTC+12:00) Auckland, Wellington",
  },
  { value: "UTC+12", label: "(UTC+12:00) Coordinated Universal Time+12" },
  { value: "Fiji Standard Time", label: "(UTC+12:00) Fiji" },
  {
    value: "Chatham Islands Standard Time",
    label: "(UTC+12:45) Chatham Islands",
  },
  { value: "Tonga Standard Time", label: "(UTC+13:00) Nuku'alofa" },
  { value: "Samoa Standard Time", label: "(UTC+13:00) Samoa" },
  {
    value: "Line Islands Standard Time",
    label: "(UTC+14:00) Kiritimati Island",
  },
];

export const statusType = [
  { value: "PENDING", color: "bg-yellow-500", label: "Pending" },
  { value: "PARTIALLY", color: "bg-orange-500", label: "Partially" },
  { value: "COMPLETE", color: "bg-green-500", label: "Complete" },
  { value: "NA", color: "bg-gray-400", label: "N/A" },
];

export const prepCategoriesList: Array<
  AmazonInboundUpdatePrepDetailsItemRequest["prepCategory"]
> = [
  "ADULT",
  "BABY",
  "FC_PROVIDED",
  "FRAGILE",
  "GRANULAR",
  "HANGER",
  "LIQUID",
  "PERFORATED",
  "SET",
  "SHARP",
  "SMALL",
  "TEXTILE",
  "UNKNOWN",
  "NONE",
];

export const prepTypesList: Array<
  NonNullable<AmazonInboundUpdatePrepDetailsItemRequest["prepTypes"]>[number]
> = [
  "ITEM_BLACK_SHRINKWRAP",
  "ITEM_BLANKSTK",
  "ITEM_BOXING",
  "ITEM_BUBBLEWRAP",
  "ITEM_CAP_SEALING",
  "ITEM_DEBUNDLE",
  "ITEM_HANG_GARMENT",
  "ITEM_LABELING",
  "ITEM_NO_PREP",
  "ITEM_POLYBAGGING",
  "ITEM_RMOVHANG",
  "ITEM_SETCREAT",
  "ITEM_SETSTK",
  "ITEM_SIOC",
  "ITEM_SUFFOSTK",
  "ITEM_TAPING",
];

export const SpecTypes: { [x: string]: string } = {
  NUMBER: "Number",
  DATE: "Date",
  LIST: "List",
  DECIMAL: "Decimal",
  LONG_TEXT: "Text",
  BOOLEAN: "Toggle",
  URL: "Url",
};
